import { useCallback, useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { UserContext } from '../../context';
import { StoryCard } from '../../services';
import { addCardToBookmarks, removeCardFromBookmarks } from '../../slices';
import { IconLabel } from '../components';
import { getCssVar, isAdminLayout } from '../utils';
import { useAppDispatch } from './useAppDispatch';
import { useFreeLoginPopup } from './useFreeLoginPopup';

interface CardBookmarksArgs {
  storyCard: StoryCard;
  iconSize?: number;
  className?: string;
}

export const useCardBookmarks = ({ storyCard, iconSize = 20, className }: CardBookmarksArgs) => {
  const { t } = useTranslation();

  const { freeLoginModal, freeLoginHandler } = useFreeLoginPopup();

  const { isAnonymous } = useContext(UserContext).userProfile;

  const { id, storyId, isBookmarked } = storyCard;

  const { t: ctxT } = useTranslation('', {
    keyPrefix: isBookmarked ? 'removeFromBookmarks' : 'addToBookmarks',
  });

  const dispatch = useAppDispatch();

  const [bookmarksToggling, setBookmarksToggling] = useState(false);

  const onBookmarksToggle = useCallback(async () => {
    if (isAnonymous) {
      freeLoginHandler();
      return;
    }

    setBookmarksToggling(true);

    try {
      await toast.promise(
        dispatch(
          (isBookmarked ? removeCardFromBookmarks : addCardToBookmarks)({
            storyId,
            storyCardId: id,
          })
        ).unwrap(),
        { pending: ctxT('pending'), success: ctxT('success'), error: ctxT('error') }
      );
    } catch (_) {
      toast(ctxT('error'));
    } finally {
      setBookmarksToggling(false);
    }
  }, [ctxT, dispatch, freeLoginHandler, id, isAnonymous, isBookmarked, storyId]);

  const cardBookmarks = useMemo(() => {
    if (isAdminLayout()) {
      return null;
    }

    return (
      <>
        <IconLabel
          className={className}
          iconId={`bookmarks${isBookmarked ? '-filled' : ''}`}
          iconSize={iconSize}
          ariaLabel={t(`cardActions.${isBookmarked ? 'remove' : 'add'}-bookmark`)}
          color={getCssVar(`--action-pane-icon${isBookmarked ? '-hover' : ''}-color`)}
          hoverColor={getCssVar('--action-pane-icon-hover-color')}
          onClick={onBookmarksToggle}
          disabled={bookmarksToggling}
        />
        {freeLoginModal}
      </>
    );
  }, [bookmarksToggling, className, freeLoginModal, iconSize, isBookmarked, onBookmarksToggle, t]);

  return { cardBookmarks };
};
